import { type FC, type ReactNode, useCallback } from 'react';
import { type StreamForStreamDashboard } from 'Lessons';
import FrontRoyalSpinner from 'FrontRoyalSpinner';
import { type CourseCard } from '../StreamDashboard.types';
import { useStreamDashboardPage } from '../hooks/useStreamDashboardPage';
import { useDeferHeavyContentOnMobile } from '../hooks/useDeferHeavyContentOnMobile';
import { CertificateBox } from './CertificateBox';
import { ShareStreamSidebar } from './ShareStreamSidebar';
import { KeyTermsSection } from './KeyTermsSection';
import { StreamDashboardColumns } from './StreamDashboardColumns';
import { StreamDashboardModals } from './StreamDashboardModals';
import '../locales/streamDashboard/streamDashboard-en.json';

/*
    The stream dashboard, given a loaded stream. StreamDashboardComponent picks the loader and hands the
    result here, so this component never deals with loading or with the offline branch, and
    useStreamDashboardPage holds the decisions.
*/

type Props = {
    stream: StreamForStreamDashboard;
    recommendedCourses: CourseCard[];
    relatedCourses: CourseCard[];
    onOpenCourse: (streamId: string) => void;
    formattedExamOpenTime: string | null;
    brandName: string;
    linkedInButton?: ReactNode;
    externalLinks?: ReactNode;
    footer?: ReactNode;
};

export const StreamDashboardPage: FC<Props> = ({
    stream,
    recommendedCourses,
    relatedCourses,
    onOpenCourse,
    formattedExamOpenTime,
    brandName,
    linkedInButton,
    externalLinks,
    footer,
}) => {
    const page = useStreamDashboardPage(stream);
    const deferHeavyContent = useDeferHeavyContentOnMobile();

    /*
        The certificate renders in the main column below md and in the sidebar above it, which is what the
        old template did with two copies. They are not interchangeable: the legacy stylesheet sized the
        main-column one by height and the sidebar one by width, so each gets its own placement.

        Only one is laid out at any width, and this keeps the bounce on that one. The ref goes on the
        wrapper rather than on CertificateBox, which does not forward one.
    */
    const setCertificateRef = useCallback(
        (element: HTMLDivElement | null) => {
            if (element && element.getClientRects().length > 0) page.certificateRef.current = element;
        },
        [page.certificateRef],
    );

    const renderCertificate = (placement: 'main-column' | 'sidebar') =>
        page.showCertificate ? (
            <div ref={setCertificateRef}>
                <CertificateBox
                    certificateImageSrc={page.certificateImageSrc!}
                    isDownloading={page.isDownloading}
                    allowDownload={page.allowDownload}
                    onDownload={page.downloadCertificate}
                    linkedInButton={linkedInButton}
                    allowLinkedIn={page.allowLinkedIn}
                    placement={placement}
                    className="mb-7.5"
                />
            </div>
        ) : null;

    return (
        <div className="stream-dashboard dashboard">
            <div className="dashboard-container">
                {/* The share rail, pinned outside the columns on wide screens. The sidebar carries a second
                    copy for the range between md and lg, which is what the old template did. */}
                {page.canShare && !stream.exam && (
                    <ShareStreamSidebar onShare={page.shareStream} variant="rail" className="max-lg:hidden" />
                )}

                <StreamDashboardColumns
                    stream={stream}
                    page={page}
                    recommendedCourses={recommendedCourses}
                    relatedCourses={relatedCourses}
                    onOpenCourse={onOpenCourse}
                    formattedExamOpenTime={formattedExamOpenTime}
                    certificate={renderCertificate('main-column')}
                    sidebarCertificate={renderCertificate('sidebar')}
                    deferHeavyContent={deferHeavyContent}
                />
            </div>

            {/* The full-width white band. It is left out when there are no key terms or links. It tests keyTermCount, not
                keyTermsReady, so the band does not drop in late. */}
            {/* Held back until the mobile route animation finishes, as hidingExtraContentFromMobile
                did. The spinner stands in for the band, the assigned reading and the footer, which is
                the set the old template swapped out. */}
            {deferHeavyContent && <FrontRoyalSpinner />}

            {!deferHeavyContent && (page.keyTermCount > 0 || !!externalLinks) && (
                <div className="border-b border-slate-grey-200 bg-white pb-9 sm:pb-14">
                    {/* The flex gap supplies the 30px between the sections, so a lone section leaves no trailing space. */}
                    <div className="dashboard-container flex flex-col gap-7.5">
                        {page.keyTermsReady && page.keyTermCount > 0 && (
                            <KeyTermsSection
                                stream={stream}
                                keyTermsByLesson={page.keyTermsByLesson}
                                isCollapsed={page.hasHiddenKeyTerms}
                                onShowAll={page.showAllKeyTerms}
                                onKeyTermClick={lesson => page.tryToLaunchLesson(lesson, 'stream_dashboard_key_term')}
                                brandName={brandName}
                            />
                        )}

                        {externalLinks}
                    </div>
                </div>
            )}

            {!deferHeavyContent && footer}

            <StreamDashboardModals
                brandName={brandName}
                upsellOpen={page.upsellOpen}
                onCloseUpsell={() => page.setUpsellOpen(false)}
                lockedMessage={page.lockedMessage}
                onCloseLocked={() => page.setLockedMessage(null)}
                pendingTimedLaunch={page.pendingTimedLaunch}
                timeLimitHours={page.timeLimitHours}
                onConfirmTimedLaunch={() => {
                    if (!page.pendingTimedLaunch) return;
                    page.launchLesson(page.pendingTimedLaunch.lesson, page.pendingTimedLaunch.linkId);
                    page.setPendingTimedLaunch(null);
                }}
                onCancelTimedLaunch={() => page.setPendingTimedLaunch(null)}
                prerequisitesNotMet={page.prerequisitesNotMet}
                prerequisitesReasonTitle={page.prerequisitesReasonTitle}
                prerequisitesReasonMessage={page.prerequisitesReasonMessage}
                onClosePrerequisites={page.leaveForDashboard}
            />
        </div>
    );
};
